'use client'

import Image from 'next/image'
import { useState } from 'react'

interface OptimizedImageProps {
  src: string
  alt: string
  srcSet?: string
  sizes?: string
  width?: number
  height?: number
  className?: string
  fill?: boolean
  priority?: boolean
  quality?: number
  style?: React.CSSProperties
  aspectRatio?: string
  fetchPriority?: 'high' | 'low' | 'auto' // Add fetchPriority prop
  [key: string]: any
}

export default function OptimizedImage({
  src,
  alt,
  srcSet,
  sizes,
  width,
  height,
  className = '',
  fill = false,
  priority = false,
  quality = 85,
  style,
  aspectRatio = '16/9',
  fetchPriority = 'auto',
  ...props
}: OptimizedImageProps) {
  const [imgSrc, setImgSrc] = useState(src)
  const [error, setError] = useState(false)
  const [hasErrored, setHasErrored] = useState(false)

  const handleError = () => {
    if (!hasErrored) {
      setHasErrored(true)
      setImgSrc('/images/tin-bong-da/default.svg')
    }
  }

  // Tính toán sizes responsive tốt hơn
  const responsiveSizes = sizes || 
    '(max-width: 480px) 100vw, (max-width: 768px) 100vw, (max-width: 1024px) 50vw, (max-width: 1200px) 33vw, 25vw'

  // Nếu có srcSet từ WordPress, sử dụng img tag với responsive styles
  if (srcSet && !fill) {
    return (
      <div 
        className={`relative w-full overflow-hidden ${className}`}
        style={{ 
          aspectRatio: aspectRatio,
          ...style 
        }}
      >
        <img
          src={imgSrc}
          alt={alt}
          srcSet={error ? undefined : srcSet}
          sizes={error ? undefined : responsiveSizes}
          className={`absolute inset-0 w-full h-full object-cover transition-transform duration-300 hover:scale-105 ${error ? 'opacity-50' : ''}`}
          onError={handleError}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : fetchPriority}
          decoding="async"
          {...props}
        />
      </div>
    )
  }

  // Nếu dùng fill, wrap trong container với aspect ratio
  if (fill) {
    return (
      <div 
        className={`relative w-full overflow-hidden ${className}`}
        style={{ 
          aspectRatio: aspectRatio,
          ...style 
        }}
      >
        <Image
          fill
          src={imgSrc}
          alt={alt}
          className={`object-cover transition-transform duration-300 hover:scale-105 ${error ? 'opacity-50' : ''}`}
          onError={handleError}
          priority={priority}
          quality={quality}
          sizes={responsiveSizes}
          {...props}
        />
      </div>
    )
  }

  // Fallback cho Next.js Image với kích thước cố định
  return (
    <div 
      className={`relative w-full overflow-hidden ${className}`}
      style={{ 
        aspectRatio: aspectRatio,
        maxWidth: width ? `${width}px` : '100%',
        ...style 
      }}
    >
      <Image
        width={width || 800}
        height={height || 400}
        src={imgSrc}
        alt={alt}
        className={`w-full h-full object-cover transition-transform duration-300 hover:scale-105 ${error ? 'opacity-50' : ''}`}
        onError={handleError}
        priority={priority}
        quality={quality}
        sizes={responsiveSizes}
        {...props}
      />
    </div>
  )
} 